import { useQueryClient } from '@tanstack/react-query'
import { useIsLoadingWithPermission, usePermissions } from 'contexts/permissions.tsx'
import BillingSettingsV2 from 'pages/CustomerCardTab/BillingSettings/BillingSettingsV2'
import type React from 'react'
import { useMemo, useState } from 'react'
import { useCustomerCardAccounts } from '../../api/react-query'
import { CARD_ACCOUNTS } from '../../api/react-query/keys.ts'
import type { BackOfficeCustomerDtoV2 } from '../../api/swagger/definitions/backoffice'
import CardPayouts from '../../components/CardPayouts.tsx'
import CardPETab from '../../components/CardPETab.tsx'
import TextContainer from '../../components/TextContainer.tsx'
import { CustomerTransactionsTable } from '../../features/customer-transactions-table'
import { TabSets } from '../../helpers/TabsHelpers.ts'
import FigmaBox from '../../mynt-components/components/FigmaBox.tsx'
import MyntLoader from '../../mynt-components/components/MyntLoader.tsx'
import Tabs from '../../mynt-components/components/Tabs.tsx'
import { TextKeys } from '../../tiger/libs/TextRepository.ts'
import { Invoices } from '../invoices'
import CustomerCardTabAutoTopup from './CustomerCardTabAutoTopup.tsx'
import CustomerCardTabChangeStatusModal from './CustomerCardTabChangeStatusModal.tsx'
import CustomerCardTabCreditOnCard from './CustomerCardTabCreditOnCard.tsx'
import CustomerCardTabLowBalanceAlert from './CustomerCardTabLowBalanceAlert.tsx'
import CustomerCardTabOverview from './CustomerCardTabOverview.tsx'

interface CustomerCardTabProps {
  customerId: string
  customer: BackOfficeCustomerDtoV2
  patchCustomerDetails: (data: any) => Promise<any>
}

const CustomerCardTab: React.FC<CustomerCardTabProps> = ({ customerId, customer }) => {
  const queryClient = useQueryClient()

  const permissions = {
    overview: usePermissions('customers.cards.overview.view'),
    enfuce: usePermissions('customers.cards.overview.enfuce'),
    transactions: usePermissions('customers.cards.transactions.view'),
    payouts: usePermissions('customers.cards.payouts'),
    invoices: usePermissions('customers.cards.invoices'),
    pe: usePermissions('customers.cards.pe'),
    autoTopUp: usePermissions('customers.cards.autoTopUp'),
    lowBalanceAlert: usePermissions('customers.cards.lowBalanceAlert'),
    creditOnCard: usePermissions('customers.cards.creditOnCard'),
    billingSettings: usePermissions('customers.cards.billing.view'),
  }

  const [isChangeStatusModalOpen, setIsChangeStatusModalOpen] = useState(false)

  const {
    data: cardAccounts,
    isLoading: _isLoadingCardAccounts,
    isFetching: _isFetchingCardAccounts,
  } = useCustomerCardAccounts(customerId, { retry: false, retryOnMount: false, enabled: permissions.enfuce })
  const currentCardAccount = useMemo(() => (cardAccounts && cardAccounts?.length > 0 ? cardAccounts[0] : undefined), [cardAccounts])

  const isLoadingCardAccounts = useIsLoadingWithPermission(_isLoadingCardAccounts, 'customers.cards.overview.enfuce')
  const isFetchingCardAccounts = useIsLoadingWithPermission(_isFetchingCardAccounts, 'customers.cards.overview.enfuce')

  if (isLoadingCardAccounts || isFetchingCardAccounts) {
    return <MyntLoader fullWidth />
  }

  return (
    <FigmaBox fullWidth>
      <Tabs
        fullPage
        enableNestedTabs
        alwaysShowNavHeadings
        tabKeyNameSpace={TabSets.CUSTOMER_CARD}
        tabs={[
          {
            labelElement: <TextContainer textKey={TextKeys.tabNotSelected} text={'Overview'} />,
            contentElement: (
              <CustomerCardTabOverview
                customer={customer}
                currentCardAccount={currentCardAccount}
                setIsChangeStatusModalOpen={setIsChangeStatusModalOpen}
              />
            ),
            enable: permissions.overview,
          },
          {
            labelElement: <TextContainer textKey={TextKeys.tabNotSelected} text={'Transactions'} />,
            contentElement: <CustomerTransactionsTable customerId={customerId} />,
            enable: permissions.transactions,
          },
          {
            labelElement: <TextContainer textKey={TextKeys.tabNotSelected} text={'Payouts'} />,
            contentElement: <CardPayouts customerId={customerId} />,
            enable: permissions.payouts,
          },
          {
            labelElement: <TextContainer textKey={TextKeys.tabNotSelected} text={'Invoices'} />,
            contentElement: <Invoices customerId={customerId} page={'CUSTOMER'} />,
            enable: permissions.invoices,
          },
          {
            labelElement: <TextContainer textKey={TextKeys.tabNotSelected} text={'PE'} />,
            contentElement: <CardPETab customerId={customerId} />,
            enable: permissions.pe,
          },
          {
            labelElement: <TextContainer textKey={TextKeys.tabNotSelected} text={TextKeys.autoTopUpHeading.characters} />,
            contentElement: <CustomerCardTabAutoTopup customerId={customerId} setIsChangeStatusModalOpen={setIsChangeStatusModalOpen} />,
            enable: permissions.autoTopUp,
          },
          {
            labelElement: <TextContainer textKey={TextKeys.tabNotSelected} text={TextKeys.lowBalanceAlertHeading.characters} />,
            contentElement: (
              <CustomerCardTabLowBalanceAlert customerId={customerId} setIsChangeStatusModalOpen={setIsChangeStatusModalOpen} />
            ),
            enable: permissions.lowBalanceAlert,
          },
          {
            labelElement: <TextContainer textKey={TextKeys.tabNotSelected} text={'Credit on card'} />,
            contentElement: <CustomerCardTabCreditOnCard customerId={customerId} />,
            enable: permissions.creditOnCard,
          },
          {
            labelElement: <TextContainer textKey={TextKeys.tabNotSelected} text={'Billing settings'} />,
            contentElement: <BillingSettingsV2 customerId={customerId} />,
            enable: permissions.billingSettings,
          },
        ]}
      />
      {!!currentCardAccount && (
        <CustomerCardTabChangeStatusModal
          isOpen={isChangeStatusModalOpen}
          setIsModalOpen={setIsChangeStatusModalOpen}
          customerId={customerId}
          cardAccount={currentCardAccount}
          onSuccess={async () => queryClient.invalidateQueries([CARD_ACCOUNTS, customerId])}
        />
      )}
    </FigmaBox>
  )
}

export default CustomerCardTab
